import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Switch, Tag, Space, Popconfirm,
  message, Input, Modal, Form,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, EyeOutlined, StarOutlined,
} from "@ant-design/icons";
import {
  fetchFlows, deleteFlow, toggleFlow, createFlow,
} from "../../../../../core/data/redux/slices/WabaAutomationSlice";
import api from "../../../../../core/axios/axiosInstance";

const FlowList = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { flows, loading } = useSelector((s) => s.wabaAutomation);

  const [search, setSearch]           = useState("");
  const [createModal, setCreateModal] = useState(false);
  const [form]                        = Form.useForm();

  // Submissions drawer
  const [submissionsModal, setSubmissionsModal] = useState(false);
  const [submissions, setSubmissions]           = useState([]);
  const [submissionFlow, setSubmissionFlow]     = useState(null);
  const [subLoading, setSubLoading]             = useState(false);

  // CSAT drawer
  const [csatModal, setCsatModal]   = useState(false);
  const [csatFlow, setCsatFlow]     = useState(null);
  const [csatData, setCsatData]     = useState([]);
  const [csatAvg, setCsatAvg]       = useState(null);
  const [csatLoading, setCsatLoading] = useState(false);

  useEffect(() => { dispatch(fetchFlows()); }, [dispatch]);

  const handleDelete = async (id) => {
    await dispatch(deleteFlow(id)).unwrap();
    message.success("Deleted");
  };

  const handleToggle = async (id) => {
    await dispatch(toggleFlow(id)).unwrap();
  };

  const handleCreate = async () => {
    const values = await form.validateFields();
    const result = await dispatch(createFlow({ name: values.name, description: values.description })).unwrap();
    setCreateModal(false);
    navigate(`/whatsapp/automation/flows/${result._id}`);
  };

  const openSubmissions = async (flow) => {
    setSubmissionFlow(flow);
    setSubLoading(true);
    setSubmissionsModal(true);
    try {
      const res = await api.get(`/api/waba/automation/flows/${flow._id}/submissions`);
      setSubmissions(res.data.data || []);
    } catch { message.error("Failed to load submissions"); }
    finally { setSubLoading(false); }
  };

  const openCsat = async (flow) => {
    setCsatFlow(flow);
    setCsatLoading(true);
    setCsatModal(true);
    setCsatData([]);
    setCsatAvg(null);
    try {
      const res = await api.get(`/api/waba/automation/flows/${flow._id}/csat`);
      setCsatData(res.data.data || []);
      setCsatAvg(res.data.average);
    } catch { message.error("Failed to load CSAT ratings"); }
    finally { setCsatLoading(false); }
  };

  const STAR_LABELS = { 5: "Excellent", 4: "Good", 3: "Average", 2: "Below average", 1: "Poor" };
  const STAR_COLORS = { 5: "#52c41a", 4: "#73d13d", 3: "#faad14", 2: "#fa8c16", 1: "#ff4d4f" };

  const csatColumns = [
    { title: "Phone", dataIndex: "contactPhone", key: "phone", width: 150 },
    {
      title: "Rating", dataIndex: "rating", key: "rating", width: 160,
      render: (v) => (
        <span style={{ color: STAR_COLORS[v], fontWeight: 600 }}>
          {"⭐".repeat(v)} <span style={{ fontSize: 11, color: "#6b7280" }}>{STAR_LABELS[v]}</span>
        </span>
      ),
    },
    {
      title: "Score", dataIndex: "rating", key: "score", width: 80,
      render: (v) => <Tag color={STAR_COLORS[v]} style={{ color: "#fff", fontWeight: 700 }}>{v}/5</Tag>,
    },
    { title: "Date", dataIndex: "createdAt", key: "date", render: (v) => new Date(v).toLocaleString() },
  ];

  const filtered = flows.filter((f) =>
    f.name?.toLowerCase().includes(search.toLowerCase())
  );

  const subColumns = [
    { title: "Phone",   dataIndex: "contactPhone", key: "phone" },
    { title: "Type",    dataIndex: "contactType",  key: "type",   render: (v) => <Tag>{v}</Tag> },
    { title: "Status",  dataIndex: "status",       key: "status", render: (v) => <Tag color={v === "completed" ? "green" : "orange"}>{v}</Tag> },
    {
      title: "Answers", dataIndex: "answers", key: "answers",
      render: (answers) => (
        <div>
          {(answers || []).map((a, i) => (
            <div key={i} style={{ fontSize: 12 }}>
              <span style={{ color: "#6b7280" }}>{a.question || `Step ${i+1}`}:</span>{" "}
              <strong>{String(a.answer || "—")}</strong>
            </div>
          ))}
        </div>
      ),
    },
    { title: "Date", dataIndex: "completedAt", key: "date", render: (v) => v ? new Date(v).toLocaleString() : "—" },
  ];

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <strong>{v}</strong> },
    {
      title: "Active", key: "active",
      render: (_, r) => <Switch checked={r.isActive} onChange={() => handleToggle(r._id)} size="small" />,
    },
    { title: "Created", dataIndex: "createdAt", key: "created", render: (v) => new Date(v).toLocaleDateString() },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button icon={<EditOutlined />}   size="small" onClick={() => navigate(`/whatsapp/automation/flows/${r._id}`)}>Edit</Button>
          <Button icon={<EyeOutlined />}    size="small" onClick={() => openSubmissions(r)}>Submissions</Button>
          <Button icon={<StarOutlined />}   size="small" onClick={() => openCsat(r)} style={{ color: "#f59e0b", borderColor: "#f59e0b" }}>CSAT</Button>
          <Popconfirm title="Delete this automation?" onConfirm={() => handleDelete(r._id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0 }}>Automations</h3>
          <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>Build automated conversation flows</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>
          Create Automation
        </Button>
      </div>

      <Input
        prefix={<SearchOutlined />}
        placeholder="Search automations"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 320, marginBottom: 16 }}
      />

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 10 }}
      />

      {/* Create modal */}
      <Modal
        title="New Automation"
        open={createModal}
        onOk={handleCreate}
        onCancel={() => setCreateModal(false)}
        okText="Create & Open Builder"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Flow Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Lead Qualification" />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* CSAT Ratings modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span>⭐ CSAT Ratings — {csatFlow?.name}</span>
            {csatAvg !== null && (
              <span style={{
                background: "#fef3c7", border: "1px solid #fcd34d",
                borderRadius: 20, padding: "2px 12px", fontSize: 13, fontWeight: 700, color: "#92400e",
              }}>
                Avg {csatAvg} / 5
              </span>
            )}
          </div>
        }
        open={csatModal}
        onCancel={() => setCsatModal(false)}
        footer={null}
        width={700}
      >
        {csatData.length === 0 && !csatLoading && (
          <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
            No ratings yet for this flow.
          </div>
        )}
        <Table
          dataSource={csatData}
          columns={csatColumns}
          rowKey="_id"
          loading={csatLoading}
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      {/* Submissions modal */}
      <Modal
        title={`Submissions — ${submissionFlow?.name}`}
        open={submissionsModal}
        onCancel={() => setSubmissionsModal(false)}
        footer={null}
        width={800}
      >
        <Table
          dataSource={submissions}
          columns={subColumns}
          rowKey="_id"
          loading={subLoading}
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};

export default FlowList;
